import { hcp, shape } from './deck.js';
import { explainBid } from './sayc.js';
/**
 * Bridges the SAYC explainer (teaching material) and bid grading: checks
 * whether a hand actually satisfies what a call conventionally promises.
 *
 * The AI grader is a policy network with one preferred call per position; a
 * textbook-correct alternative can get ~0% probability (e.g. a limit raise
 * when the model splinters). `saycConsistent` lets the grader recognize such
 * calls as sound instead of scoring them like nonsense. It only vouches for
 * calls whose meaning is exact AND carries machine-checkable requirements
 * (`BidMeaning.req`) the hand meets — everything else returns false, leaving
 * the model's judgment untouched.
 */
export function satisfiesConstraint(hand, req) {
    const points = hcp(hand);
    if (req.minHcp !== undefined && points < req.minHcp)
        return false;
    if (req.maxHcp !== undefined && points > req.maxHcp)
        return false;
    const lengths = shape(hand); // suit order ♠♥♦♣ — flip from strain order ♣♦♥♠
    if (req.suits) {
        for (const s of req.suits) {
            const len = lengths[3 - s.strain];
            if (s.min !== undefined && len < s.min)
                return false;
            if (s.max !== undefined && len > s.max)
                return false;
        }
    }
    if (req.balanced) {
        if (lengths.some((len) => len < 2))
            return false;
        if (lengths.filter((len) => len === 2).length > 1)
            return false;
    }
    return true;
}
/** True iff `call` has an exact, constraint-carrying SAYC meaning that `hand` satisfies. */
export function saycConsistent(hand, dealer, calls, call) {
    const m = explainBid(dealer, calls, call);
    return m !== null && m.exact && m.req !== undefined && satisfiesConstraint(hand, m.req);
}
/**
 * True iff `call` has an exact, constraint-carrying SAYC meaning that `hand`
 * FAILS — i.e. making this call would promise partner something the hand
 * doesn't hold (a weak two on a 5-card suit, a 1♠ opening on 11 HCP, a pass
 * of a hand too strong to pass). Calls whose meaning is generic or carries no
 * machine-checkable requirements (artificial conventions like Stayman,
 * transfers, Blackwood; doubles) are never violations — we can't check them,
 * so we don't forbid them.
 */
export function saycViolation(hand, dealer, calls, call) {
    const m = explainBid(dealer, calls, call);
    return m !== null && m.exact && m.req !== undefined && !satisfiesConstraint(hand, m.req);
}
//# sourceMappingURL=advisor.js.map